import React from 'react';

import {
	Box,
	Divider,
	Stack,
	type SxProps,
	type Theme,
	Typography,
} from '@mui/material';

import { OWNERSHIP_DESCRIPTION_SUCCINT } from '@ivy/constants/organization';
import { gql, type FragmentType, getFragmentData } from '@ivy/gql/types';
import { formatInteger } from '@ivy/lib/formatting/number';
import { combineSx } from '@ivy/lib/styling/sx';

const OrgAtAGlance_OrgFDoc = gql(/* GraphQL */ `
	fragment OrgAtAGlance_Org on organization {
		id
		ownershipStructure: ownership_structure
		contractsAgg: contracts_aggregate(where: { active: { _eq: true } }) {
			aggregate {
				count
			}
		}
	}
`);

export interface OrgAboutProps {
	org: FragmentType<typeof OrgAtAGlance_OrgFDoc>;
	sx?: SxProps<Theme>;
}

const OrgAbout = ({ org: rawOrg, sx }: OrgAboutProps) => {
	const org = getFragmentData(OrgAtAGlance_OrgFDoc, rawOrg);

	return (
		<Box
			sx={combineSx(
				{
					display: 'flex',
					flexDirection: {
						xs: 'column',
						sm: 'row',
					},
					justifyContent: 'space-evenly',
					alignItems: 'center',
				},
				sx,
			)}
		>
			<Stack
				alignItems='center'
				spacing={1}
				flex='1 1 50%'
				pr={{ sm: 4 }}
				mb={{ xs: 6, sm: 0 }}
			>
				<Box
					component='img'
					src='https://assets.ivyclinicians.io/images/hospital-icon-shaded.svg'
					sx={{
						width: '48px',
						height: '48px',
					}}
				/>
				<Typography
					variant='h6'
					component='p'
					color='text.icon'
					textAlign='center'
				>
					{org.contractsAgg.aggregate?.count
						? `${formatInteger(org.contractsAgg.aggregate.count)} ${
								org.contractsAgg.aggregate?.count === 1 ? 'ED' : 'EDs'
						  }`
						: 'N/A'}
				</Typography>
				<Typography variant='body2' color='text.icon' textAlign='center'>
					EM practice size
				</Typography>
			</Stack>
			<Box flex='0 0 auto'>
				<Divider
					orientation='vertical'
					sx={{
						height: '100px',
						display: {
							xs: 'none',
							sm: 'block',
						},
					}}
				/>
				<Divider
					orientation='horizontal'
					sx={{
						width: '100px',
						mb: 6,
						display: {
							sm: 'none',
						},
					}}
				/>
			</Box>
			<Stack alignItems='center' spacing={1} flex='1 1 50%' pl={{ sm: 4 }}>
				{/* TODO: convert to assets */}
				<Box
					component='img'
					src='https://assets.ivyclinicians.io/images/facility-icon-shaded.svg'
					sx={{
						width: '48px',
						height: '48px',
					}}
				/>
				<Typography
					variant='h6'
					component='p'
					color='text.icon'
					textAlign='center'
				>
					{OWNERSHIP_DESCRIPTION_SUCCINT[org.ownershipStructure] || 'N/A'}
				</Typography>
				<Typography variant='body2' color='text.icon' textAlign='center'>
					Ownership structure
				</Typography>
			</Stack>
		</Box>
	);
};

export default OrgAbout;
